/**
 * Hook to manage a GQL query.
 *
 * For details on usage, see https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/1958347015/useQuery.
 */
import {
    FetchPolicy,
    toGqlOperation,
    useCachedEffect,
    type Result,
} from "@khanacademy/wonder-blocks-data";
import {documentNode, type GqlOperation} from "@repo/data-gql-core";
import {Errors, KAError} from "@repo/logging";

import type {GqlQueryOptions} from "./types.ts";
import {useGql} from "./use-gql.ts";
import {useRequestIdBuilder} from "./use-request-id-builder.ts";

const DefaultQueryScope = "useQuery";

/**
 * Hook to fetch and cache a query.
 *
 * A hook that fetches a query from GraphQL and stores the result in the shared
 * (client-side) cache. See also: useFastlyQuery.
 */
export const useQuery = <
    TData extends Record<any, any>,
    TVariables extends Record<any, any>,
>(
    operation: GqlOperation<TData, TVariables>,
    {
        curriculum,
        module,
        retainResultOnChange = false,
        skip = false,
        targetLocale,
        variables,
        fetchPolicy = FetchPolicy.CacheBeforeNetwork,
        cacheUpdated,
    }: GqlQueryOptions<TData, TVariables> = Object.freeze({}),
): [Result<TData>, () => void] => {
    if (process.env.NODE_ENV !== "production") {
        const wbdOperation = toGqlOperation(documentNode(operation));
        if (wbdOperation.type !== "query") {
            throw new KAError(
                "Mutations are not valid for useQuery",
                Errors.InvalidInput,
            );
        }
    }

    // Normally, we rely on the gqlFetch to handle combining our partial
    // context with the defaults specified with GqlRouter. However, in this
    // case we need that info here to be able to get a request ID.
    const requestIdBuilder = useRequestIdBuilder();
    const requestId = requestIdBuilder(operation, variables, {
        module,
        targetLocale,
        curriculum,
    });

    // Now we perform the actual querying.
    const gqlFetch = useGql();
    const result = useCachedEffect<TData>(
        requestId,
        () =>
            gqlFetch(operation, {
                variables,
                context: {
                    curriculum,
                    module,
                    targetLocale,
                },
            }),
        {
            onResultChanged: cacheUpdated,
            retainResultOnChange,
            skip,
            scope: DefaultQueryScope,
            fetchPolicy,
        },
    );
    return result;
};
